import { useMemo } from 'react'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { IconButton, Stack, Typography } from '@mui/material'
import { InvoiceFieldAvailability } from '@/hooks/useDocumentInvoiceData'
import { PDPurchaseOrder } from '@/types/po-match'
import { RelatedInvoice } from '@/utils/related-invoices'
import RelatedInvoiceCard, { TiedPOLineItem } from './RelatedInvoiceCard'

type RelatedInvoicesListProps = {
  invoices: RelatedInvoice[]
  /** The POs loaded in the view, used to label each tie's PO side. */
  purchaseOrders: PDPurchaseOrder[]
  availability: InvoiceFieldAvailability
  expandedInvoiceIds: Set<string>
  onToggleExpanded: (invoiceId: string) => void
  showInvoices: boolean
  onShowInvoicesChange: (show: boolean) => void
  /** Registers the points on each card its connectors start from. */
  anchorRefCallback?: (anchorId: string) => (el: HTMLElement | null) => void
  /** Called with the hovered card's invoice id, or null when none is. */
  onHoveredInvoiceChange?: (invoiceId: string | null) => void
}

/** The other invoices tied to the focused invoice's POs, listed under its card. Renders nothing when there are none. */
export default function RelatedInvoicesList({
  invoices,
  purchaseOrders,
  availability,
  expandedInvoiceIds,
  onToggleExpanded,
  showInvoices,
  onShowInvoicesChange,
  anchorRefCallback,
  onHoveredInvoiceChange,
}: RelatedInvoicesListProps) {
  const { poNumbers, poLineItems } = useMemo(() => {
    const poNumbers = new Map<string, string>()
    const poLineItems = new Map<string, TiedPOLineItem>()
    purchaseOrders.forEach((po) => {
      poNumbers.set(po.external_id, po.number)
      po.line_items.forEach((lineItem) => {
        poLineItems.set(lineItem.external_id, {
          poNumber: po.number,
          description: lineItem.description || null,
        })
      })
    })
    return { poNumbers, poLineItems }
  }, [purchaseOrders])

  if (invoices.length === 0) return null

  return (
    <Stack spacing={1.5} alignItems="flex-end" sx={{ mt: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ width: '85%' }}
      >
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ lineHeight: 1.5 }}
        >
          Other invoices attached to these POs
        </Typography>
        <IconButton
          size="small"
          aria-label={
            showInvoices ? 'Hide other invoices' : 'Show other invoices'
          }
          aria-expanded={showInvoices}
          onClick={() => onShowInvoicesChange(!showInvoices)}
        >
          {showInvoices ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )}
        </IconButton>
      </Stack>
      {showInvoices &&
        invoices.map((invoice) => (
          <RelatedInvoiceCard
            key={invoice.id}
            invoice={invoice}
            poLineItems={poLineItems}
            poNumbers={poNumbers}
            availability={availability}
            expanded={expandedInvoiceIds.has(invoice.id)}
            onToggleExpanded={() => onToggleExpanded(invoice.id)}
            anchorRefCallback={anchorRefCallback}
            onHoverChange={(hovering) =>
              onHoveredInvoiceChange?.(hovering ? invoice.id : null)
            }
          />
        ))}
    </Stack>
  )
}
